import React from 'react';
import { BookOpen, Check, ClipboardList } from 'lucide-react';
import { LessonExerciseHierarchyIcon } from '../admin/LessonExerciseHierarchyIcon';

export type LessonJourneyContentIconKind = 'lesson' | 'exercise' | 'inlineExercise' | 'quiz';

export function lessonJourneyIconKindFromStepId(stepId: string): LessonJourneyContentIconKind | null {
  if (stepId === 'review' || stepId === 'mark-passed') return 'lesson';
  if (stepId.startsWith('inline-exercise-')) return 'inlineExercise';
  if (stepId.startsWith('exercise-')) return 'exercise';
  if (stepId.startsWith('quiz-')) return 'quiz';
  return null;
}

function PassTickBadge() {
  return (
    <span
      className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-[10px] w-[10px] items-center justify-center rounded-full bg-green-600 text-white ring-1 ring-white dark:ring-gray-900"
      aria-hidden
    >
      <Check className="h-2 w-2" strokeWidth={3} />
    </span>
  );
}

/** Same iconography as the admin content hierarchy (lesson / exercise / quiz / inline exercise). */
export const LessonJourneyContentIcon: React.FC<{
  kind: LessonJourneyContentIconKind;
  className?: string;
  /** Green completion tick overlay when the step is done. */
  showPassTick?: boolean;
}> = ({ kind, className = '', showPassTick = false }) => {
  switch (kind) {
    case 'lesson':
      return (
        <span className={`relative inline-flex h-4 w-4 shrink-0 items-center justify-center ${className}`}>
          <BookOpen
            className="h-4 w-4 text-gray-500 dark:text-gray-400"
            aria-hidden
          />
          {showPassTick ? <PassTickBadge /> : null}
        </span>
      );
    case 'exercise':
      return <LessonExerciseHierarchyIcon className={className} showPassTick={showPassTick} />;
    case 'inlineExercise':
      return <LessonExerciseHierarchyIcon inline className={className} showPassTick={showPassTick} />;
    case 'quiz':
      return (
        <span className={`relative inline-flex h-4 w-4 shrink-0 items-center justify-center ${className}`}>
          <ClipboardList
            className="h-4 w-4 text-purple-600 dark:text-purple-400"
            aria-hidden
          />
          {showPassTick ? <PassTickBadge /> : null}
        </span>
      );
    default:
      return null;
  }
};
